import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useDemoStoreOptional } from "@/contexts/DemoStoreContext";
import { useToast } from "@/hooks/use-toast";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";

export interface AlertData {
  id: string;
  title: string | null;
  message: string | null;
  severity: string | null;
  alert_type: string | null;
  is_read: boolean;
  resolved: boolean;
  created_at: string;
  reference_id: string | null;
  reference_type: string | null;
}

const ALERT_TYPE_TO_PREF: Record<string, string> = {
  late_return: "notify_late_return",
  return_today: "notify_return_today",
  insurance_expiry: "notify_insurance_expiry",
  maintenance_due: "notify_maintenance_due",
  pickup_ready: "notify_pickup_ready",
  vehicle_idle: "notify_vehicle_idle",
  damage_flagged: "notify_damage_flagged",
  task_escalation: "notify_task_escalation",
};

export function useAlerts() {
  const { isDemo } = useDemo();
  const store = useDemoStoreOptional();
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { business } = useBusinessSettings();

  const profilePrefs = (profile as any)?.notification_preferences as Record<string, boolean> | undefined;
  const businessPrefs = (business as any)?.notification_preferences as Record<string, boolean> | undefined;
  const notifPrefs = profilePrefs || businessPrefs;

  const query = useQuery({
    queryKey: ["alerts", profile?.business_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alerts")
        .select("*")
        .eq("business_id", profile!.business_id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as AlertData[];
    },
    enabled: !isDemo && !!profile?.business_id,
    staleTime: 60_000,
    refetchInterval: 2 * 60 * 1000,
  });

  const rawAlerts = isDemo ? (store?.alerts ?? []) as AlertData[] : (query.data ?? []);

  const alerts = rawAlerts.filter((a) => {
    const prefKey = ALERT_TYPE_TO_PREF[a.alert_type || ""];
    if (!prefKey || !notifPrefs) return true;
    return notifPrefs[prefKey] !== false;
  });

  const unreadCount = alerts.filter((a) => !a.is_read).length;

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo && store) { store.markAlertRead(id); return; }
      const { error } = await supabase.from("alerts").update({ is_read: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { if (!isDemo) qc.invalidateQueries({ queryKey: ["alerts"] }); },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      if (isDemo && store) { store.markAllAlertsRead(); return; }
      const { error } = await supabase.from("alerts").update({ is_read: true }).eq("business_id", profile!.business_id).eq("is_read", false);
      if (error) throw error;
    },
    onSuccess: () => { if (!isDemo) qc.invalidateQueries({ queryKey: ["alerts"] }); },
  });

  const resolveAlert = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo && store) { store.resolveAlert(id); return; }
      const { error } = await supabase.from("alerts").update({ resolved: true, resolved_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { if (!isDemo) qc.invalidateQueries({ queryKey: ["alerts"] }); },
  });

  return { alerts, unreadCount, isLoading: !isDemo && query.isLoading, markRead, markAllRead, resolveAlert };
}
